import React, { useEffect, useRef, useState } from "react";

/**
 * Drag a file out of the browser as a real file, via the Chromium-only
 * `DownloadURL` drag type (Chrome/Edge; not Safari/Firefox). Dragging is only
 * enabled where it actually works — see {@link supportsFileDragOut}.
 */

/** True only in Chromium desktop browsers, where `DownloadURL` drag-out works. */
let cachedSupport: boolean | null = null;
export const supportsFileDragOut = (): boolean => {
    if (cachedSupport !== null) {
        return cachedSupport;
    }
    if (typeof navigator === "undefined") {
        return false;
    }
    const ua = navigator.userAgent;
    const isChromium = /Chrome|Chromium|Edg\//.test(ua) && !/Firefox|FxiOS/.test(ua);
    const isIOS = /iPhone|iPad|iPod/.test(ua) || /CriOS/.test(ua);
    cachedSupport = isChromium && !isIOS;
    return cachedSupport;
};

interface IDownloadDragOptions {
    /** Absolute URL the file is fetched from on drop. */
    url: string;
    /** Suggested file name including extension, e.g. "Rechnung-2026-001.pdf". */
    filename: string;
    /** MIME type, e.g. "application/pdf". Derived from the extension when omitted. */
    mime?: string;
}

/** Minimal extension → MIME map for the file types rpforms produces/stores. */
const MIME_BY_EXTENSION: { [ext: string]: string } = {
    pdf: "application/pdf",
    png: "image/png",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    gif: "image/gif",
    webp: "image/webp",
    svg: "image/svg+xml",
    heic: "image/heic",
    doc: "application/msword",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    xls: "application/vnd.ms-excel",
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    csv: "text/csv",
    txt: "text/plain",
    zip: "application/zip",
    xml: "application/xml",
};

/** Best-effort MIME from a file name, defaulting to a generic binary type. */
export const guessMimeFromName = (filename: string): string => {
    const dot = filename.lastIndexOf(".");
    const ext = dot >= 0 ? filename.slice(dot + 1).toLowerCase() : "";
    return MIME_BY_EXTENSION[ext] || "application/octet-stream";
};

/** Load the file before dragging so Chrome does not run a second URL download. */
export const fetchDragBlob = async (url: string, mime: string): Promise<Blob> => {
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`Unable to load dragged file (${response.status})`);
    }

    const blob = await response.blob();
    if (blob.size === 0) {
        throw new Error("Unable to drag an empty file");
    }

    return blob.type === mime ? blob : new Blob([blob], { type: mime });
};

/**
 * Wires a `dragstart` event so the dragged element becomes a real file on drop.
 * Safe to call unconditionally — it no-ops on non-Chromium browsers because the
 * `DownloadURL` type is simply ignored there. Callers should still gate the
 * `draggable` attribute on {@link supportsFileDragOut} to hide the affordance.
 */
export const setDownloadDrag = (
    e: React.DragEvent,
    { url, filename, mime }: IDownloadDragOptions
): void => {
    // The DownloadURL format is `mime:filename:url`, split on the first two
    // colons. The URL may contain colons (https://…); the filename must not — and
    // a slash would be read as a path separator by the OS — so strip both
    // (plus newlines). e.g. invoice names like "2026/001".
    const safeName = filename.replace(/[:\r\n/\\]+/g, "_").trim() || "download";
    // mime_type may be null/empty in the data — never emit "null" as the type.
    const effectiveMime = (mime && mime.trim()) || guessMimeFromName(safeName);
    e.dataTransfer.setData("DownloadURL", `${effectiveMime}:${safeName}:${url}`);
    e.dataTransfer.effectAllowed = "copy";
};

interface IDraggableFileProps extends Partial<IDownloadDragOptions> {
    filename: string;
    children: React.ReactNode;
    className?: string;
    /** Wrapper tag, defaults to span (inline). */
    as?: "span" | "div";
    style?: React.CSSProperties;
    /**
     * When set, the (short-lived, signed) download URL is fetched lazily on
     * hover instead of using the static `url` prop, then loaded into a local
     * blob URL for the actual drag.
     */
    getUrl?: () => Promise<string>;
}

/**
 * Inline wrapper that makes its children draggable as a downloadable file.
 * Renders an unmodified, non-draggable wrapper on unsupported browsers.
 */
export const DraggableFile: React.FC<IDraggableFileProps> = ({
    url,
    filename,
    mime,
    children,
    className,
    as: As = "span",
    style,
    getUrl,
}) => {
    const enabled = supportsFileDragOut();
    const effectiveMime = (mime && mime.trim()) || guessMimeFromName(filename);
    const preparedUrl = useRef<string | null>(null);
    const requestVersion = useRef(0);
    const loading = useRef(false);
    const [loadState, setLoadState] = useState<"idle" | "loading" | "ready" | "error">("idle");

    useEffect(() => {
        requestVersion.current += 1;
        loading.current = false;
        if (preparedUrl.current) {
            URL.revokeObjectURL(preparedUrl.current);
            preparedUrl.current = null;
        }
        setLoadState("idle");

        return () => {
            requestVersion.current += 1;
            loading.current = false;
            if (preparedUrl.current) {
                URL.revokeObjectURL(preparedUrl.current);
                preparedUrl.current = null;
            }
        };
    }, [filename, url]);

    const prepareFile = () => {
        if (!enabled || preparedUrl.current || loading.current) {
            return;
        }

        const version = requestVersion.current;
        loading.current = true;
        setLoadState("loading");
        const sourceUrl = getUrl ? getUrl() : Promise.resolve(url);
        sourceUrl
            .then((resolvedUrl) => {
                if (!resolvedUrl) {
                    throw new Error("Unable to resolve dragged file");
                }
                return fetchDragBlob(resolvedUrl, effectiveMime);
            })
            .then(
                (blob) => {
                    if (version !== requestVersion.current) {
                        return;
                    }
                    preparedUrl.current = URL.createObjectURL(blob);
                    loading.current = false;
                    setLoadState("ready");
                },
                () => {
                    if (version !== requestVersion.current) {
                        return;
                    }
                    loading.current = false;
                    setLoadState("error");
                }
            );
    };

    const startDrag = (e: React.DragEvent) => {
        if (!preparedUrl.current) {
            e.preventDefault();
            prepareFile();
            return;
        }
        setDownloadDrag(e, {
            url: preparedUrl.current,
            filename,
            mime: effectiveMime,
        });
    };

    const title =
        loadState === "loading"
            ? "Datei wird vorbereitet"
            : loadState === "error"
            ? "Datei konnte nicht vorbereitet werden"
            : "Zum Speichern ziehen";

    return (
        <As
            className={className}
            draggable={enabled || undefined}
            onMouseEnter={enabled ? prepareFile : undefined}
            onMouseDown={enabled ? prepareFile : undefined}
            onFocus={enabled ? prepareFile : undefined}
            onDragStart={enabled ? startDrag : undefined}
            title={enabled ? title : undefined}
            style={enabled ? { cursor: "pointer", ...style } : style}
        >
            {children}
        </As>
    );
};
